/* =====================================================================================
   Crew.

   Every call here goes through a SECURITY DEFINER function in the `app` schema. The `hr`
   schema is not exposed to the API and must stay that way, so there is no client query that
   names hr.employees. If these functions did not exist, the browser would have no way to
   reach the workforce at all, which is the intended default.

   Two levels of exposure, deliberately different:

     crew_list()    name, role, status, competence gaps. Safe on a shared screen.
     crew_record()  salary, date of birth, address, NI, visa. A separate act, and logged.

   The second is not a "detail view" of the first. Opening someone's record writes a row to
   hr.record_access_log recording who looked and when, because "who has been looking at my
   salary" is a question an employee is entitled to ask.
   ===================================================================================== */

import { BACKEND } from './db';
import { TENANT } from '@tenant';

export interface CrewMember {
  out_employee_id: string;
  out_member_id: string | null;
  out_name: string;
  out_job_title: string | null;
  out_department: string | null;
  out_status: string;
  out_start_date: string | null;
  out_primary_role: string | null;
  out_gaps: number;
  /* The reporting line and the expense-approver override, since 0091. Expense claims go to
     the line manager derived from reports_to; the override wins when set. */
  out_reports_to_id: string | null;
  out_reports_to_name: string | null;
  out_expense_approver_id: string | null;
  out_expense_approver_name: string | null;
}

export interface CrewRole {
  out_role_id: string; out_code: string; out_title: string;
  out_department: string | null; out_required: number; out_holders: number;
}

export interface Competence {
  out_competence_id: string; out_code: string; out_name: string;
  out_category: string | null; out_validity_months: number | null;
}

export interface RoleRequirement {
  out_competence_id: string; out_code: string; out_name: string;
  out_criticality: 'required' | 'desirable'; out_due_within: number | null;
}

/** The sensitive record. Named fully rather than `any` so a field cannot be added to a form
    without also being added to the allow-list in migration 0019. */
export interface EmployeeRecord {
  out_employee_id: string;
  out_employee_number: string | null;
  out_legal_first: string; out_legal_last: string; out_preferred_name: string | null;
  out_date_of_birth: string | null;
  out_personal_email: string | null; out_personal_phone: string | null;
  out_home_address: Record<string, string> | null;
  out_emergency_contact: Record<string, string> | null;
  out_employment_type: string | null; out_status: string;
  out_start_date: string | null; out_probation_end: string | null; out_end_date: string | null;
  out_notice_days: number | null;
  out_job_title: string | null; out_department: string | null; out_work_location: string | null;
  out_fte: number | null; out_contracted_hours: number | null;
  out_annual_salary: number | null; out_salary_currency: string | null;
  out_holiday_days: number | null;
  out_ni_number: string | null; out_tax_code: string | null; out_payroll_id: string | null;
  out_pension_scheme: string | null; out_pension_opt_out: boolean | null;
  out_rtw_checked: boolean | null; out_rtw_check_date: string | null; out_rtw_expiry: string | null;
  out_visa_required: boolean | null; out_visa_type: string | null; out_visa_expiry: string | null;
}

export interface AccessEntry {
  out_who: string; out_action: 'viewed' | 'edited';
  out_fields: string[] | null; out_when: string;
}

const live = () => BACKEND === 'supabase';

async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc(fn, args);
  if (error) throw new Error(tidy(error.message));
  return (data ?? []) as T[];
}

/* The functions raise readable exceptions. PostgREST wraps them in its own prose, so strip
   the wrapper and show what the function author actually wrote. */
function tidy(message: string): string {
  return message.replace(/^.*?(?:ERROR|error):\s*/i, '').trim() || message;
}

/** Whether to offer Crew administration at all. Presentation only: the gate is in Postgres. */
/* One function per question, matching the database exactly. Asked of the server rather than
   inferred from a role list held in the browser, because the server is what will refuse. */
export async function crewGates(): Promise<{
  view: boolean; admin: boolean; access: boolean; grant: boolean;
}> {
  if (BACKEND !== 'supabase') return { view: true, admin: true, access: true, grant: true };
  const { supabase } = await import('./supabase');
  const s = supabase().schema('app');
  const [v, a, ac, g] = await Promise.all([
    s.rpc('may_view_crew'), s.rpc('may_admin_crew'),
    s.rpc('may_admin_access'), s.rpc('may_grant_roles'),
  ]);
  return { view: v.data === true, admin: a.data === true,
           access: ac.data === true, grant: g.data === true };
}

export async function mayAdminCrew(): Promise<boolean> {
  if (!live()) return true;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('may_admin_crew');
  if (error) return false;
  return Boolean(data);
}

/* Local mode: the tenant's own workforce. Without this a demonstration tenant shows an empty
   Crew module, which reads as a broken screen rather than as a company with nobody in it. */
export const listCrew = async (): Promise<CrewMember[]> =>
  live() ? rpc<CrewMember>('crew_list') : (TENANT.seed.crew as CrewMember[]);
export const listRoles       = () => rpc<CrewRole>('crew_roles');
export const listCompetences = () => rpc<Competence>('crew_competences');
export const roleRequirements = (roleId: string) =>
  rpc<RoleRequirement>('crew_role_requirements', { p_role_id: roleId });
export const accessHistory = (employeeId: string) =>
  rpc<AccessEntry>('crew_access_history', { p_employee_id: employeeId });

export async function openRecord(employeeId: string): Promise<EmployeeRecord | null> {
  const rows = await rpc<EmployeeRecord>('crew_record', { p_employee_id: employeeId });
  return rows[0] ?? null;
}

async function call(fn: string, args: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').rpc(fn, args);
  if (error) throw new Error(tidy(error.message));
}

export const addCrew = (a: {
  firstName: string; lastName: string; jobTitle: string; startDate: string;
  department?: string; employmentType?: string;
}) => call('crew_add', {
  p_first_name: a.firstName, p_last_name: a.lastName, p_job_title: a.jobTitle,
  p_start_date: a.startDate, p_department: a.department || null,
  p_employment_type: a.employmentType || 'employee',
});

export const addRole = (code: string, title: string, department?: string) =>
  call('crew_add_role', { p_code: code, p_title: title, p_department: department || null });

export const addCompetence = (code: string, name: string, category: string, validityMonths?: number) =>
  call('crew_add_competence', {
    p_code: code, p_name: name, p_category: category,
    p_validity_months: validityMonths ?? null,
  });

export const requireCompetence = (
  roleId: string, competenceId: string,
  criticality: 'required' | 'desirable' = 'required', dueWithin = 90,
) => call('crew_require_competence', {
  p_role_id: roleId, p_competence_id: competenceId,
  p_criticality: criticality, p_due_within: dueWithin,
});

export const unrequireCompetence = (roleId: string, competenceId: string) =>
  call('crew_unrequire_competence', { p_role_id: roleId, p_competence_id: competenceId });

export const assignRole = (employeeId: string, roleId: string, primary = true) =>
  call('crew_assign_role', { p_employee_id: employeeId, p_role_id: roleId, p_primary: primary });

/** Patch of allowed fields only. Anything else is refused by name in migration 0019. */
export const setReportingLine = (employeeId: string, reportsToId: string | null) =>
  call('crew_set_reporting_line', { p_employee_id: employeeId, p_reports_to_id: reportsToId });

export const setExpenseApprover = (memberId: string, approverId: string | null) =>
  call('crew_set_expense_approver', { p_member_id: memberId, p_approver_id: approverId });

export const updateRecord = (employeeId: string, patch: Record<string, unknown>) =>
  call('crew_update_record', { p_employee_id: employeeId, p_patch: patch });

/* -------------------------------------------------------------------------------------
   Access: invitations and roles.

   Separate from the employment record on purpose. Joining the payroll and gaining access to
   the commercial pipeline are two different decisions, and one should not silently imply
   the other. Inviting requires owner or admin; notably NOT hr, because administering the
   workforce and granting system access are different powers.
   ------------------------------------------------------------------------------------- */

export const ROLES = [
  { key: 'owner',      label: 'Owner',      note: 'Everything, including HR and role grants.' },
  { key: 'admin',      label: 'Admin',      note: 'System administration. Not HR.' },
  { key: 'slt',        label: 'Leadership', note: 'Publishes notices and events. Sees the whole diary, including patent deadlines.' },
  { key: 'commercial', label: 'Commercial', note: 'Atlas, Pipeline, Channel, Contracts. The usual field role.' },
  { key: 'finance',    label: 'Finance',    note: 'Ledger, credit control, contract commercials.' },
  { key: 'hr',         label: 'HR',         note: 'Crew only, including salary and personnel data.' },
  { key: 'quality',    label: 'Quality',    note: 'Quality and Docket.' },
  /* The named person outside the management line that Conflicts and Whistleblowing both require.
     Reads the workforce and the canon, approves policy, edits nothing. Deliberately absent from
     app.can_write: an independent director with edit rights over the commercial model is not
     independent of it. */
  { key: 'sid',        label: 'Senior independent director',
    note: 'Reads Crew and Canon and approves policy. Changes nothing else. The independent route for whistleblowing and conflicts.' },
  { key: 'readonly',   label: 'Read only',  note: 'Reads across enabled modules, changes nothing.' },
] as const;

export interface Member {
  id: string; email: string; full_name: string | null; job_title: string | null;
  status: string; auth_user_id: string | null; claimed_at: string | null;
  invited_at: string | null; roles: string[];
}

export async function listMembers(): Promise<Member[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const [{ data: members }, { data: roles }] = await Promise.all([
    supabase().schema('app').from('members')
      .select('id, email, full_name, job_title, status, auth_user_id, claimed_at, invited_at')
      .order('full_name'),
    supabase().schema('app').from('member_roles').select('member_id, role'),
  ]);
  const byMember = new Map<string, string[]>();
  for (const r of roles ?? []) {
    byMember.set(r.member_id, [...(byMember.get(r.member_id) ?? []), r.role]);
  }
  return (members ?? []).map((m: any) => ({ ...m, roles: byMember.get(m.id) ?? [] }));
}

export async function inviteMember(i: {
  email: string; fullName: string; jobTitle?: string; roles: string[]; employeeId?: string | null;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').rpc('invite_member', {
    p_email: i.email, p_full_name: i.fullName,
    p_job_title: i.jobTitle ?? null, p_roles: i.roles,
    p_employee_id: i.employeeId ?? null,
  });
  if (error) throw new Error(tidy(error.message));
  const { forgetMemberNames } = await import('./supabase');
  forgetMemberNames();
}

/* Create their Supabase login and email them a sign-in link, in one step.

   NO SERVICE ROLE KEY IS INVOLVED, which is the whole point of doing it this way. Creating a
   user through the Auth admin API needs the service key, that key must never reach the browser,
   and putting it behind an edge function is a deployment and a secret to rotate for something
   the anon key can already do safely.

   Sending a one-time-password link to an address creates the account if it does not exist and
   emails a link to it. That is exactly the invitation that "Send invitation" used to imply and
   never actually sent. It grants nothing on its own: without the member record and roles written
   alongside it, whoever follows the link signs in and sees nothing at all. */
export async function createLoginAndEmailLink(email: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().auth.signInWithOtp({
    email: email.trim(),
    options: { emailRedirectTo: window.location.origin, shouldCreateUser: true },
  });
  if (error) throw new Error(tidy(error.message));
}

/** Roles are granted and revoked individually so the audit trail records each decision. */
export async function grantRole(memberId: string, role: string) {
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { data: org } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  const { error } = await supabase().schema('app').from('member_roles').insert({
    member_id: memberId, org_id: org?.id, role, granted_by: me?.id, granted_at: new Date().toISOString(),
  });
  if (error && error.code !== '23505') throw new Error(tidy(error.message));
}

export async function revokeRole(memberId: string, role: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('member_roles')
    .delete().eq('member_id', memberId).eq('role', role);
  if (error) throw new Error(tidy(error.message));
}

export async function revokeInvitation(memberId: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('revoke_invitation', {
    p_member_id: memberId,
  });
  if (error) throw new Error(tidy(error.message));
  return data as string;
}

/** Called once after sign-in: links an invited email to the account that just signed in. */
export async function claimMembership(): Promise<string> {
  if (!live()) return 'demo';
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('claim_membership');
  if (error) return 'error';
  return (data as string) ?? 'unknown';
}
